import { zodResolver } from '@hookform/resolvers/zod';
import { Building2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { api, mensagemDeErro } from '@/lib/api-client';
import { onboardingSchema, type OnboardingForm } from '@/schemas/onboarding.schema';

interface OnboardingResponse {
  empresaId: string;
  usuarioId: string;
  email: string;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [erroApi, setErroApi] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OnboardingForm>({
    resolver: zodResolver(onboardingSchema),
    mode: 'onTouched',
  });

  async function onSubmit(values: OnboardingForm) {
    setErroApi(null);
    try {
      await api.post<OnboardingResponse>('/onboarding/empresa', {
        codigoInstalacao: values.codigoInstalacao,
        empresa: {
          razaoSocial: values.razaoSocial,
          nomeFantasia: values.nomeFantasia,
          cnpj: values.cnpj || undefined,
          telefone: values.telefone || undefined,
        },
        gestor: {
          nome: values.nome,
          email: values.email,
          senha: values.senha,
        },
      });
      toast.success('Empresa cadastrada — faça login para continuar.');
      navigate('/login', { replace: true });
    } catch (err) {
      setErroApi(mensagemDeErro(err));
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4 py-10">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Cadastrar empresa</CardTitle>
              <CardDescription>
                Use o código de instalação para criar a primeira conta de gestor.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <CardContent className="space-y-6">
            {erroApi ? (
              <Alert variant="destructive" data-testid="erro-api">
                <AlertDescription>{erroApi}</AlertDescription>
              </Alert>
            ) : null}

            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Dados da empresa
              </h2>
              <FormField
                label="Código de instalação"
                htmlFor="codigoInstalacao"
                required
                error={errors.codigoInstalacao?.message}
                hint="32 caracteres hexadecimais — gerado pelo administrador via pnpm db:seed."
              >
                <Input
                  id="codigoInstalacao"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  {...register('codigoInstalacao')}
                />
              </FormField>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  label="Razão social"
                  htmlFor="razaoSocial"
                  required
                  error={errors.razaoSocial?.message}
                >
                  <Input id="razaoSocial" {...register('razaoSocial')} />
                </FormField>

                <FormField
                  label="Nome fantasia"
                  htmlFor="nomeFantasia"
                  required
                  error={errors.nomeFantasia?.message}
                >
                  <Input id="nomeFantasia" {...register('nomeFantasia')} />
                </FormField>

                <FormField
                  label="CNPJ"
                  htmlFor="cnpj"
                  error={errors.cnpj?.message}
                  hint="Opcional. 14 dígitos, com ou sem máscara."
                >
                  <Input id="cnpj" {...register('cnpj')} />
                </FormField>

                <FormField label="Telefone" htmlFor="telefone" error={errors.telefone?.message}>
                  <Input id="telefone" {...register('telefone')} />
                </FormField>
              </div>
            </section>

            <Separator />

            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Gestor responsável
              </h2>
              <FormField label="Nome completo" htmlFor="nome" required error={errors.nome?.message}>
                <Input id="nome" autoComplete="name" {...register('nome')} />
              </FormField>

              <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
                <Input id="email" type="email" autoComplete="email" {...register('email')} />
              </FormField>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  label="Senha"
                  htmlFor="senha"
                  required
                  error={errors.senha?.message}
                  hint="Mínimo 10 caracteres, 1 maiúscula e 1 número."
                >
                  <Input
                    id="senha"
                    type="password"
                    autoComplete="new-password"
                    {...register('senha')}
                  />
                </FormField>
                <FormField
                  label="Confirmação de senha"
                  htmlFor="confirmacaoSenha"
                  required
                  error={errors.confirmacaoSenha?.message}
                >
                  <Input
                    id="confirmacaoSenha"
                    type="password"
                    autoComplete="new-password"
                    {...register('confirmacaoSenha')}
                  />
                </FormField>
              </div>
            </section>
          </CardContent>

          <CardFooter className="flex flex-col items-stretch gap-3">
            <Button type="submit" disabled={isSubmitting} data-testid="botao-cadastrar">
              {isSubmitting ? 'Cadastrando…' : 'Cadastrar empresa'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Já tem conta?{' '}
              <Link to="/login" className="underline">
                Fazer login
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
